import {BaseDashlet} from "../../lq_js_common/dashboard/base_dashlet";
import {DashboardGraph} from "../../graphs/dashboard_graph";
import {GraphOptionsBuilder} from "../../lq_js_common/e_charts/chart_builder";
import {scaleNumber} from "../../helpers/scaling";
import {getGraphColor} from "../../helpers/dark_mode";
import {safeBigIntToNumber} from "../../helpers/number_coerce";

export class ShaperLivePackets extends BaseDashlet {
    constructor(slot) {
        super(slot);
        this.rendered = false;
        this.down = [];
        this.up = [];
        this.maxPoints = 120;
    }

    title() { return "Live Packets/s"; }
    supportsZoom() { return false; }
    tooltip() { return "<h5>Live Packets</h5><p>Current packets per second. Upload inverted.</p>"; }

    buildContainer() {
        let base = super.buildContainer();
        // Apply a border by default; color updated on shaper_detail
        base.classList.add('border');
        // Status line shown until first live sample arrives
        const status = document.createElement('div');
        status.id = this.id + '_status';
        status.classList.add('text-muted');
        status.style.fontSize = '0.85rem';
        status.style.marginTop = '4px';
        status.innerText = 'Connecting (requires LibreQoS V1.5 RC2 or greater)';
        base.appendChild(status);
        const g = this.graphDiv();
        g.style.height = "100%";
        base.appendChild(g);
        return base;
    }

    setup() {
        super.setup();
        this.graph = new LivePacketsGraph(this.graphDivId());
        this.graph.update([], []);
    }

    channelRequests() { return ["ShaperDetail", "ShaperLiveThroughput"]; }

    onMessage(msg) {
        if (msg.Data.target === "shaper_detail") {
            // Update border color based on last_seen like all_shapers
            const entries = Object.entries(msg.Data.blob);
            if (entries.length > 0) {
                const [_, value] = entries[0];
                const nowUTC = value.now_server;
                const lastSeen = value.header.last_seen;
                const timeDiff = nowUTC - lastSeen;
                const container = document.getElementById(this.id);
                container.classList.remove('border-success','border-warning','border-danger');
                if (timeDiff < 300) container.classList.add('border-success');
                else if (timeDiff < 600) container.classList.add('border-warning');
                else container.classList.add('border-danger');
            }
            return;
        }
        if (msg.Data.target !== "shaper_live_throughput") return;
        const p = msg.Data.blob; // live payload
        const down = safeBigIntToNumber(p.packets_down || 0);
        const up = -safeBigIntToNumber(p.packets_up || 0);
        this.down.push(down);
        this.up.push(up);
        if (this.down.length > this.maxPoints) { this.down.shift(); this.up.shift(); }
        const status = document.getElementById(this.id + '_status');
        if (status) status.style.display = 'none';
        this.graph.update(this.down, this.up);
    }
}

class LivePacketsGraph extends DashboardGraph {
    constructor(id) {
        super(id);
        this.option = new GraphOptionsBuilder()
            .withLeftGridSize(60)
            .withSequenceAxis(0, 0)
            .withScaledAbsYAxis("Packets", 50)
            .withEmptyLegend()
            .withEmptySeries()
            .build();
        // Tighten grid and hide legend/axis labels for sparkline look
        this.option.grid = { left: 45, right: 10, top: 4, bottom: 4 };
        this.option.legend = { show: false };
        this.option.xAxis.axisLabel = { show: false };
        this.option && this.chart.setOption(this.option);
    }

    update(down, up) {
        this.chart.hideLoading();
        const x = Array.from({length: down.length}, (_, i) => i);
        this.option.xAxis.data = x;
        this.option.series = [];
        this.option.legend.data = [];

        const mk = (name, data, color, stack) => ({
            type: 'line', data, name, smooth: true, showSymbol: false,
            lineStyle: { width: 1 }, itemStyle: { color }, areaStyle: { color, opacity: 0.2 }, stack
        });
        const liveColor = getGraphColor(1);
        const s0 = mk('Packets Down', down, liveColor, 'down');
        const s1 = mk('Packets Up', up, liveColor, 'up');
        this.option.series.push(s0, s1);

        this.option.tooltip = {
            trigger: 'axis',
            axisPointer: { type: 'cross', label: { backgroundColor: 'rgba(30,30,30,0.92)', color: '#fff', fontFamily: 'inherit', formatter: function(p){ return scaleNumber(Math.abs(p.value), 2) + ' pkts/s'; } } },
            backgroundColor: 'rgba(30,30,30,0.92)', textStyle: { fontSize: 10, color: '#fff', fontFamily: 'inherit' },
            formatter: (params) => {
                const idx = params[0].dataIndex;
                const vals = [down[idx]||0, Math.abs(up[idx]||0)];
                return `<div style=\"font-size:10px;\"><strong>Packets</strong><br/>Down: ${scaleNumber(vals[0],0)} pkts/s<br/>Up: ${scaleNumber(vals[1],0)} pkts/s</div>`;
            }
        };

        this.chart.setOption(this.option);
    }
}
